/**
 * CANONICAL ROUTE REQUIREMENT MAP
 *
 * Single source of truth for what each route (or route group) requires
 * before it may render content. Used by route-level guards and the
 * global resolution boundary.
 *
 * DO NOT scatter route-requirement logic across route files.
 */

import type { AppRole } from "@/contexts/AuthContext";

export type RouteRequirement = {
  /** Require authenticated user */
  requireAuth: boolean;
  /** Require activated membership (profile + membership_selected_at) */
  requireActivation: boolean;
  /** Allowed roles (empty = any authenticated role) */
  allowedRoles: AppRole[];
  /** Route is public-only (should redirect authenticated users away) */
  publicOnly: boolean;
  /**
   * Route is publicly accessible but renders auth-aware content (e.g. header
   * personalization, account CTAs). Resolution boundary will buffer rendering
   * until auth state is stable to prevent flicker.
   */
  requireStableAuth: boolean;
  /** Brief description for dev diagnostics */
  label: string;
};

// ─── Presets ────────────────────────────────────────────────────────────────

const PUBLIC: RouteRequirement = {
  requireAuth: false,
  requireActivation: false,
  allowedRoles: [],
  publicOnly: false,
  requireStableAuth: false,
  label: "public",
};

const PUBLIC_ONLY: RouteRequirement = {
  requireAuth: false,
  requireActivation: false,
  allowedRoles: [],
  publicOnly: true,
  requireStableAuth: false,
  label: "public-only (redirect if authenticated)",
};

const AUTH_ONLY: RouteRequirement = {
  requireAuth: true,
  requireActivation: false,
  allowedRoles: [],
  publicOnly: false,
  requireStableAuth: false,
  label: "auth-only (no activation required)",
};

const USER_DASHBOARD: RouteRequirement = {
  requireAuth: true,
  requireActivation: true,
  allowedRoles: ["user" as AppRole],
  publicOnly: false,
  requireStableAuth: false,
  label: "user-dashboard",
};

const ADMIN_DASHBOARD: RouteRequirement = {
  requireAuth: true,
  requireActivation: true,
  allowedRoles: ["admin" as AppRole],
  publicOnly: false,
  requireStableAuth: false,
  label: "admin-dashboard",
};

const ADIGIG_DASHBOARD: RouteRequirement = {
  requireAuth: true,
  requireActivation: true,
  allowedRoles: ["adigig" as AppRole],
  publicOnly: false,
  requireStableAuth: false,
  label: "adigig-dashboard",
};

const ONBOARDING: RouteRequirement = {
  requireAuth: true,
  requireActivation: false,
  allowedRoles: [],
  publicOnly: false,
  requireStableAuth: false,
  label: "onboarding (auth required, activation not required)",
};

// ─── Route Map ──────────────────────────────────────────────────────────────

/**
 * Resolve the requirement for a given pathname.
 * Uses prefix matching with longest-match-first priority.
 */
export function getRouteRequirement(pathname: string): RouteRequirement {
  // Exact matches first
  if (pathname === "/login" || pathname === "/signup") return PUBLIC_ONLY;
  if (pathname === "/forgot-password" || pathname === "/reset-password") return PUBLIC_ONLY;

  // Auth callbacks — need special handling (not public-only, not protected)
  if (pathname === "/auth/confirm" || pathname === "/auth/oauth-callback") return PUBLIC;
  if (pathname === "/verify-email") return PUBLIC;

  // Onboarding
  if (pathname.startsWith("/onboarding")) return ONBOARDING;

  // Preview routes (admin must be authed but activation may not apply during preview)
  if (pathname.startsWith("/preview/seo/")) return AUTH_ONLY;

  // Admin dashboard
  if (pathname === "/admin/login") return PUBLIC;
  if (pathname === "/admin/accept-invite") return PUBLIC;
  if (pathname.startsWith("/admin-access/")) return PUBLIC;
  if (pathname.startsWith("/admin")) return ADMIN_DASHBOARD;

  // AdiGig dashboard
  if (pathname === "/adigig/login") return PUBLIC;
  if (pathname === "/adigig/signup") return PUBLIC;
  if (pathname === "/adigig/pending") return AUTH_ONLY;
  if (pathname.startsWith("/adigig-access/")) return PUBLIC;
  if (pathname.startsWith("/adigig")) return ADIGIG_DASHBOARD;

  // User dashboard routes
  const userPaths = [
    "/dashboard", "/profile", "/membership", "/benefits", "/earnings",
    "/work-history", "/retirement", "/insurance", "/payments",
    "/documents", "/support", "/settings", "/referrals", "/privacy",
  ];
  for (const p of userPaths) {
    if (pathname === p || pathname.startsWith(p + "/")) return USER_DASHBOARD;
  }

  // Community (feature-flagged, treat as public for routing)
  if (pathname.startsWith("/community")) return PUBLIC;

  // Everything else is public
  return PUBLIC;
}

// ─── Exports for testing ────────────────────────────────────────────────────

export const PRESETS = {
  PUBLIC,
  PUBLIC_ONLY,
  AUTH_ONLY,
  USER_DASHBOARD,
  ADMIN_DASHBOARD,
  ADIGIG_DASHBOARD,
  ONBOARDING,
} as const;
